import React from "react";
import {
  AHIcon,
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  Image,
} from "arkh";
import { graphql, useFragment } from "react-relay";
import {
  useOpenCamera,
  useOpenImagePicker,
} from "../../../../components/mobile/useImageActionSheet";

import { TodoEditNewMainFooterResponseImage_todo$key } from "./__generated__/TodoEditNewMainFooterResponseImage_todo.graphql";

import { useTodoEditResponseImage } from "./useTodoEditResponseImage";
import { FormattedMessage } from "react-intl";
import { useLightboxControls } from "@/src/common/lightbox/LightboxProvider";
import { Platform } from "react-native";

export function TodoEditNewMainFooterResponseImage(props: {
  todo: TodoEditNewMainFooterResponseImage_todo$key;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditNewMainFooterResponseImage_todo on Todo {
        id
        canWrite
        status
        isResponseCompleted
        response {
          ... on TodoResponseImage {
            images {
              id
              sizes {
                width
                height
              }
              ...Image_image
            }
          }
        }
      }
    `,
    props.todo
  );

  const { addImage, removeImage } = useTodoEditResponseImage({
    todoId: todo.id,
  });
  const { openCamera } = useOpenCamera({
    onAddImage(uri, { width, height }) {
      addImage(uri, { width, height });
    },
  });
  const { openImagePicker } = useOpenImagePicker({
    onAddImage(uri, { width, height }) {
      addImage(uri, { width, height });
    },
  });

  const layout = useLayoutContext();

  const lightbox = useLightboxControls();

  return (
    <>
      <Box
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          marginTop: -layout.space.small,
        }}
      >
        <Box style={{ flexDirection: "row" }}>
          {todo.response?.images?.map((image, index) => {
            return (
              <Box spaceRight="small" spaceTop="small" key={image.id}>
                <TouchableBox
                  onPress={() => {
                    lightbox.openLightbox({
                      images:
                        todo.response?.images?.map((image) => ({
                          onDelete() {
                            removeImage(image.id);
                          },
                          image: {
                            type: "remote" as const,
                            image: image,
                          },
                          mediaType: "image" as const,
                          dimensions: {
                            width: image.sizes?.[0]?.width ?? 1,
                            height: image.sizes?.[0]?.height ?? 1,
                          },
                        })) ?? [],
                      index,
                    });
                  }}
                >
                  <Image
                    type="remote"
                    key={image.id}
                    image={image}
                    style={{
                      width: layout.circle.large,
                      height: layout.circle.large,
                      borderRadius: layout.radius.medium,
                      borderWidth: 2,
                      borderColor: "transparent",
                    }}
                  />
                </TouchableBox>
              </Box>
            );
          })}
        </Box>
        {todo.canWrite && todo.status !== "DONE" ? (
          <Box
            style={{
              flexDirection: "row",
            }}
            spaceTop="small"
          >
            {Platform.OS !== "web" ? (
              <>
                <InputButton
                  onPress={openCamera}
                  type="camera"
                  prominent={!todo.isResponseCompleted}
                />
                <Box spaceRight="small" />
              </>
            ) : null}
            <InputButton
              onPress={openImagePicker}
              type="image"
              prominent={!todo.isResponseCompleted}
            />
          </Box>
        ) : null}
        {!todo.isResponseCompleted ? (
          <AHText
            spaceLeft="medium"
            spaceTop="small"
            type="subtitle"
            color="text-washed"
          >
            <FormattedMessage
              defaultMessage="Submit image"
              description="Todo image response required to submit message"
            />
          </AHText>
        ) : null}
      </Box>
    </>
  );
}

function InputButton(props: {
  onPress: () => void;
  type: "image" | "camera";
  prominent: boolean;
}) {
  return (
    <TouchableBox
      borderColor={"border-primary"}
      style={{
        borderWidth: 1,
        width: 50,
        height: 50,
        justifyContent: "center",
        alignItems: "center",
      }}
      cornerRadius="medium"
      color="background"
      onPress={props.onPress}
    >
      <AHIcon
        type={props.type}
        color={props.prominent ? "primary" : "text-secondary"}
        size="medium"
      />
    </TouchableBox>
  );
}
